import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCoachTyping } from '@/hooks/use-coach-typing'
import { normalizeGlobalToPercent, scoreBandForDisplay, scoreBandStyle } from '@/lib/match-scoring'
import { cn } from '@/lib/utils'
import type { ExternalJobListingBrief } from '@/types'
import {
  Banknote,
  BookOpen,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  ExternalLink,
  Loader2,
  MapPin,
  PanelRight,
  Sparkles,
  Wifi,
} from 'lucide-react'
import { type ReactElement, useEffect, useRef, useState } from 'react'

interface MatchData {
  overall_score: number
  matched_skills?: string[]
  missing_skills?: string[]
  match_summary?: string | undefined
  global_score?: number | null
}

interface JobCardProps {
  job: ExternalJobListingBrief
  match?: MatchData | null
  onViewDetails?: (jobId: number) => void
  onAnalyzeMatch?: (jobId: number, resumeId?: number | null) => void
  analyzeResumes?: { id: number; label: string }[]
  /** Parent binds job + match; same flow as job detail "Learn all skills". */
  onLearnAll?: () => void
  learnAllPending?: boolean
  /** This job's lessons are still being generated (server) — spinner + "Curating lessons". */
  learnAllCurating?: boolean
  /** Another job/topic is generating; disable "Learn all skills" on this card. */
  learnAllBlockedWhileGeneratingElsewhere?: boolean
  /** Native tooltip when blocked while generation runs elsewhere. */
  learnAllBlockedTooltip?: string
  /** Show learn/continue row even when match has no "skills to develop" (e.g. path already created). */
  learnAllForceShow?: boolean
  learnAllError?: string | null
  learnAllLabel?: string
  onAskAbout?: (jobId: number) => void
  onGenerateResume?: (jobId: number) => void
  onRetryGenerateResume?: (jobId: number) => void
  onViewGeneratedResume?: (jobId: number) => void
  onDownloadGeneratedResume?: (jobId: number) => void
  generateResumeStatus?: string | null
  /** Percentage points vs. pre-tailoring score; shown under the match ring when ATS resume is ready */
  generateResumeDelta?: number | null
  generateResumeError?: string | null
  isAnalyzing?: boolean
  className?: string
}

function formatSalary(min?: number, max?: number, currency?: string): string | null {
  if (!min && !max) return null
  const curr = currency || 'USD'
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: curr,
    maximumFractionDigits: 0,
  })

  if (min && max) {
    return `${formatter.format(min)} - ${formatter.format(max)}`
  }
  if (min) {
    return `From ${formatter.format(min)}`
  }
  if (max) {
    return `Up to ${formatter.format(max)}`
  }
  return null
}

function formatTimeAgo(dateStr?: string): string | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return '1 day ago'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return `${Math.floor(diffDays / 30)} months ago`
}

function formatJobType(jobType?: string): string | null {
  if (!jobType) return null
  return jobType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-')
}

const APPLY_RESUME_DONE_STATUSES = new Set(['ready', 'ready_reanalysis_failed'])

// Max pills shown before "+N more" overflow chip
const MAX_MATCHED_SHOWN = 4
const MAX_MISSING_SHOWN = 3

function MatchRing({
  score,
  displayScore,
  bandStyle,
  pulsing,
}: {
  score: number
  displayScore: number
  bandStyle: ReturnType<typeof scoreBandStyle>
  pulsing?: boolean
}) {
  const size = 56
  const strokeWidth = 5
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference
  const color = bandStyle.ringHex

  return (
    <div
      className={cn('relative shrink-0 transition-transform duration-300', pulsing && 'scale-110')}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold tabular-nums" style={{ color }}>
          {displayScore}%
        </span>
      </div>
    </div>
  )
}

export function JobCard({
  job,
  match,
  onViewDetails,
  onAnalyzeMatch,
  analyzeResumes,
  onLearnAll,
  learnAllPending,
  learnAllCurating,
  learnAllBlockedWhileGeneratingElsewhere,
  learnAllBlockedTooltip,
  learnAllForceShow,
  learnAllError,
  learnAllLabel,
  onGenerateResume,
  onRetryGenerateResume,
  onDownloadGeneratedResume,
  generateResumeStatus,
  generateResumeDelta,
  generateResumeError,
  isAnalyzing,
  className,
}: JobCardProps): ReactElement {
  const animSeenKey = `pelgo:coach_anim:${job.id}`
  const [animAlreadySeen] = useState(() => {
    try {
      return localStorage.getItem(animSeenKey) === '1'
    } catch {
      return false
    }
  })

  const [skillsExpanded, setSkillsExpanded] = useState(false)
  const [showApplyConfirm, setShowApplyConfirm] = useState(false)
  const [applyConfirmed, setApplyConfirmed] = useState(false)
  const [selectedAnalyzeResumeId, setSelectedAnalyzeResumeId] = useState<number | null>(
    () => analyzeResumes?.[0]?.id ?? null,
  )

  useEffect(() => {
    setSelectedAnalyzeResumeId((prev) => {
      if (prev !== null && analyzeResumes?.some((r) => r.id === prev)) return prev
      return analyzeResumes?.[0]?.id ?? null
    })
  }, [analyzeResumes])

  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_currency)
  const normalizedGlobalPercent = normalizeGlobalToPercent(match?.global_score)
  const score = normalizedGlobalPercent ?? match?.overall_score ?? 0
  const hasScore = score > 0
  const jobType = formatJobType(job.job_type)
  const timeAgo = formatTimeAgo(job.posted_at || job.created_at)
  const isCuratingLessons = Boolean(learnAllCurating || learnAllPending)
  const showLearnAllRow =
    Boolean(hasScore) &&
    (Boolean(learnAllCurating) ||
      Boolean(learnAllForceShow) ||
      Boolean(onLearnAll && match?.missing_skills && match.missing_skills.length > 0))

  const applyResumeDone = Boolean(
    generateResumeStatus && APPLY_RESUME_DONE_STATUSES.has(generateResumeStatus),
  )
  const showGenerateResumeButton =
    Boolean(onGenerateResume) && !applyResumeDone && generateResumeStatus !== 'failed_no_resume'

  const resumeFailed =
    Boolean(generateResumeStatus?.startsWith('failed_')) && Boolean(onRetryGenerateResume)

  // Analyze strip: shown for unanalyzed jobs when resumes are available
  const showAnalyzeStrip = !match && Boolean(onAnalyzeMatch) && Boolean(analyzeResumes?.length)

  // Coach strip shows single best next action (only for already-analyzed jobs):
  // (A) generate resume → (B) learn skills once resume is done
  const resumeBoostPercent =
    typeof generateResumeDelta === 'number' && generateResumeDelta > 0 ? generateResumeDelta : null
  const showPostResumeCoach = applyResumeDone && hasScore
  const showLearnInActionBar = showLearnAllRow && !showPostResumeCoach
  const showCoachStrip =
    hasScore && (showGenerateResumeButton || resumeFailed || showPostResumeCoach)

  const band = scoreBandForDisplay(match?.global_score, score)
  const bandStyle = scoreBandStyle(band)

  // Score count-up and ring pulse when score prop changes
  const prevScoreRef = useRef(score)
  const [displayScore, setDisplayScore] = useState(score)
  const [ringPulsing, setRingPulsing] = useState(false)
  useEffect(() => {
    const from = prevScoreRef.current
    if (score === from) return
    prevScoreRef.current = score
    setRingPulsing(true)
    const pulseOff = setTimeout(() => setRingPulsing(false), 500)
    const startTime = performance.now()
    const duration = 800
    let rafId: number
    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1)
      const eased = 1 - (1 - t) ** 3
      setDisplayScore(Math.round(from + (score - from) * eased))
      if (t < 1) rafId = requestAnimationFrame(animate)
    }
    rafId = requestAnimationFrame(animate)
    return () => {
      clearTimeout(pulseOff)
      cancelAnimationFrame(rafId)
    }
  }, [score])

  // Coach typing animation — re-triggers when the suggestion text changes
  const coachText = showAnalyzeStrip
    ? 'Analyse this job to see how your resume matches'
    : showGenerateResumeButton
      ? 'Generate a tailored ATS resume to boost your match'
      : showPostResumeCoach
        ? resumeBoostPercent != null
          ? `Tailored resume covers ${resumeBoostPercent}% more key terms`
          : 'Your tailored resume is ready'
        : ''
  const {
    displayed: coachDisplayed,
    phase: coachPhase,
    iconKey,
  } = useCoachTyping(coachText, animAlreadySeen)

  useEffect(() => {
    if (coachPhase === 'done') {
      try {
        localStorage.setItem(animSeenKey, '1')
      } catch (_) {
        // localStorage may be unavailable in private browsing
      }
    }
  }, [coachPhase, animSeenKey])

  // Coverage block typing animation — starts once the learn text finishes typing
  const coverageAnimSeenKey = `pelgo:coverage_anim:${job.id}`
  const [coverageAnimAlreadySeen] = useState(() => {
    try {
      return localStorage.getItem(coverageAnimSeenKey) === '1'
    } catch {
      return false
    }
  })
  // Secondary learn text — fires after the coverage text finishes
  const learnAfterCoverageText =
    showPostResumeCoach && showLearnAllRow && coachPhase === 'done'
      ? 'Learn additional skills to further improve your resume'
      : ''
  const {
    displayed: learnDisplayed,
    phase: learnPhase,
    iconKey: learnIconKey,
  } = useCoachTyping(learnAfterCoverageText, coverageAnimAlreadySeen)

  useEffect(() => {
    if (learnPhase === 'done') {
      try {
        localStorage.setItem(coverageAnimSeenKey, '1')
      } catch (_) {
        // localStorage may be unavailable in private browsing
      }
    }
  }, [learnPhase, coverageAnimSeenKey])

  // Score tier labels and colors
  const scoreTierLabel = !hasScore ? null : `${bandStyle.label} Match`
  const scoreTierColor = !hasScore ? null : bandStyle.textClass

  // Skills with overflow
  const matchedSkills = match?.matched_skills ?? []
  const missingSkills = match?.missing_skills ?? []
  const totalSkills = matchedSkills.length + missingSkills.length
  const shownMatched = matchedSkills.slice(0, MAX_MATCHED_SHOWN)
  const extraMatched = matchedSkills.length - shownMatched.length
  const shownMissing = missingSkills.slice(0, MAX_MISSING_SHOWN)
  const extraMissing = missingSkills.length - shownMissing.length

  return (
    <div
      className={cn(
        'border rounded-2xl bg-white dark:bg-gray-900 p-5 hover:shadow-md transition-shadow overflow-hidden',
        className,
      )}
    >
      {/* Company row + posted time */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2.5">
          {job.company_logo_url ? (
            <img
              src={job.company_logo_url}
              alt={`${job.company} logo`}
              className="h-7 w-7 rounded-md object-contain bg-gray-50 border shrink-0"
              onError={(e) => {
                const target = e.currentTarget
                target.style.display = 'none'
                const fallback = target.nextElementSibling as HTMLElement
                if (fallback) fallback.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className={cn(
              'h-7 w-7 items-center justify-center rounded-md bg-gray-100 dark:bg-gray-800 shrink-0',
              job.company_logo_url ? 'hidden' : 'flex',
            )}
          >
            <Building2 className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">
            {job.company || 'Company'}
          </span>
        </div>
        {timeAgo && <span className="text-xs text-muted-foreground shrink-0">{timeAgo}</span>}
      </div>

      {/* Job title */}
      <h3 className="font-heading font-bold text-base leading-snug mb-2.5">{job.title}</h3>

      {/* Meta row: compact, single line */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-4">
        {job.location && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" />
            {job.location}
            {job.is_remote && (
              <span className="flex items-center gap-1 text-green-600 ml-0.5">
                <Wifi className="h-3 w-3" />
                Remote
              </span>
            )}
          </span>
        )}
        {jobType && (
          <span className="flex items-center gap-1">
            <Briefcase className="h-3 w-3 shrink-0" />
            {jobType}
          </span>
        )}
        {salary && (
          <span className="flex items-center gap-1">
            <Banknote className="h-3 w-3 shrink-0" />
            {salary}
          </span>
        )}
      </div>

      {/* Match block */}
      {hasScore && (
        <>
          <div
            className={cn(
              'border-t pt-4 mb-3 rounded-xl -mx-2 px-2 group',
              onViewDetails &&
                'cursor-pointer hover:bg-blue-50/40 dark:hover:bg-blue-950/10 transition-colors',
            )}
            onClick={onViewDetails ? () => onViewDetails(job.id) : undefined}
            role={onViewDetails ? 'button' : undefined}
            tabIndex={onViewDetails ? 0 : undefined}
            onKeyDown={
              onViewDetails
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') onViewDetails(job.id)
                  }
                : undefined
            }
          >
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center gap-1 shrink-0">
                <MatchRing
                  score={score}
                  displayScore={displayScore}
                  bandStyle={bandStyle}
                  pulsing={ringPulsing}
                />
                {scoreTierLabel && (
                  <span className={cn('text-xs font-semibold leading-none', scoreTierColor)}>
                    {scoreTierLabel}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                {/* Skill ratio */}
                {totalSkills > 0 && (
                  <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-1">
                    <span className="text-xs text-muted-foreground">
                      {matchedSkills.length} of {totalSkills} skills matched
                    </span>
                  </div>
                )}

                {/* Why summary: always clamped */}
                {match?.match_summary && (
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {match.match_summary}
                  </p>
                )}

                {/* View analysis indicator — below the text column */}
                {onViewDetails && (
                  <div className="flex mt-1.5">
                    <span className="text-xs font-medium text-blue-500 dark:text-blue-400 flex items-center gap-0.5 group-hover:gap-1.5 transition-all duration-150">
                      View analysis
                      <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Skills section */}
          {(matchedSkills.length > 0 || missingSkills.length > 0) && (
            <div className="flex flex-col gap-2.5 mb-4">
              {matchedSkills.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1.5 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Skills you have
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(skillsExpanded ? matchedSkills : shownMatched).map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                      >
                        {skill}
                      </span>
                    ))}
                    {!skillsExpanded && extraMatched > 0 && (
                      <button
                        type="button"
                        onClick={() => setSkillsExpanded(true)}
                        className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border hover:bg-muted/70 transition-colors cursor-pointer"
                      >
                        <ChevronDown className="h-2.5 w-2.5" />+{extraMatched} more
                      </button>
                    )}
                  </div>
                </div>
              )}

              {missingSkills.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1.5 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    Skills to develop
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(skillsExpanded ? missingSkills : shownMissing).map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-600 border border-amber-200 dark:border-amber-800"
                      >
                        {skill}
                      </span>
                    ))}
                    {!skillsExpanded && extraMissing > 0 && (
                      <button
                        type="button"
                        onClick={() => setSkillsExpanded(true)}
                        className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border hover:bg-muted/70 transition-colors cursor-pointer"
                      >
                        <ChevronDown className="h-2.5 w-2.5" />+{extraMissing} more
                      </button>
                    )}
                  </div>
                </div>
              )}

              {skillsExpanded && (extraMatched > 0 || extraMissing > 0) && (
                <button
                  type="button"
                  onClick={() => setSkillsExpanded(false)}
                  className="self-start flex items-center gap-0.5 text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                  <ChevronUp className="h-2.5 w-2.5" />
                  Show less
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Analyze strip — shown for unanalyzed jobs: resume picker + Analyse Match */}
      {showAnalyzeStrip && analyzeResumes && (
        <div className="rounded-xl p-3 mb-4 border bg-sky-50/60 dark:bg-sky-950/15 border-sky-200/70 dark:border-sky-800/60">
          <div className="flex flex-col gap-2">
            {/* Icon + animated text */}
            <div className="flex items-center gap-1.5 min-w-0">
              <Sparkles
                key={iconKey}
                className="h-3.5 w-3.5 text-sky-500 shrink-0"
                style={
                  coachPhase === 'dots'
                    ? {
                        animationName: 'spin',
                        animationDuration: '0.55s',
                        animationTimingFunction: 'ease-in-out',
                        animationIterationCount: 1,
                        animationFillMode: 'none',
                      }
                    : undefined
                }
              />
              {coachPhase === 'dots' ? (
                <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                  <span
                    className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                    style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                  />
                  <span
                    className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                    style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                  />
                  <span
                    className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                    style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                  />
                </span>
              ) : (
                <span className="text-xs text-foreground/80 min-w-0">
                  {coachDisplayed}
                  {coachPhase === 'typing' && (
                    <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                  )}
                </span>
              )}
            </div>

            {/* Resume picker + button — only visible once text has appeared */}
            {(coachPhase === 'typing' || coachPhase === 'done') && (
              <div className="flex items-center gap-2 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                <Select
                  value={selectedAnalyzeResumeId !== null ? String(selectedAnalyzeResumeId) : ''}
                  onValueChange={(v) => setSelectedAnalyzeResumeId(Number(v))}
                  disabled={Boolean(isAnalyzing)}
                >
                  <SelectTrigger className="h-7 text-xs flex-1 min-w-0 border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30">
                    <SelectValue placeholder="Select resume" />
                  </SelectTrigger>
                  <SelectContent>
                    {analyzeResumes.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs shrink-0 border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40"
                  onClick={() => onAnalyzeMatch?.(job.id, selectedAnalyzeResumeId)}
                  disabled={selectedAnalyzeResumeId === null || isAnalyzing}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Analyzing...
                    </>
                  ) : (
                    'Analyse Match'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coach strip — forward-looking: generate resume → then learn skills */}
      {showCoachStrip && (
        <div className="rounded-xl p-3 mb-4 border bg-sky-50/60 dark:bg-sky-950/15 border-sky-200/70 dark:border-sky-800/60">
          {showGenerateResumeButton ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                {/* Icon + animated text */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <Sparkles
                    key={iconKey}
                    className="h-3.5 w-3.5 text-sky-500 shrink-0"
                    style={
                      coachPhase === 'dots'
                        ? {
                            animationName: 'spin',
                            animationDuration: '0.55s',
                            animationTimingFunction: 'ease-in-out',
                            animationIterationCount: 1,
                            animationFillMode: 'none',
                          }
                        : undefined
                    }
                  />
                  {coachPhase === 'dots' ? (
                    <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                      <span
                        className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                        style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                      />
                      <span
                        className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                        style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                      />
                      <span
                        className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                        style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                      />
                    </span>
                  ) : (
                    <span className="text-xs text-foreground/80 min-w-0">
                      {coachDisplayed}
                      {coachPhase === 'typing' && (
                        <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                      )}
                    </span>
                  )}
                </div>

                {/* Generate button — only shown once text is visible */}
                {(coachPhase === 'typing' || coachPhase === 'done') && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs shrink-0 border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
                    onClick={() => onGenerateResume?.(job.id)}
                    disabled={
                      generateResumeStatus === 'preparing' ||
                      generateResumeStatus === 'generating_revision' ||
                      generateResumeStatus === 'reanalyzing'
                    }
                  >
                    {generateResumeStatus === 'preparing' ||
                    generateResumeStatus === 'generating_revision' ||
                    generateResumeStatus === 'reanalyzing' ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        {generateResumeStatus === 'reanalyzing'
                          ? 'Reanalyzing...'
                          : 'Generating...'}
                      </>
                    ) : (
                      'Generate resume'
                    )}
                  </Button>
                )}
              </div>
            </div>
          ) : showPostResumeCoach ? (
            <div className="flex flex-col gap-2">
              {/* Coverage block — green tint, types heading first when delta > 0 */}
              <div className="flex flex-col gap-0.5">
                {coachPhase === 'done' ? (
                  <div className="flex items-center justify-between gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-400">
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Sparkles key={iconKey} className="h-3.5 w-3.5 text-sky-500 shrink-0" />
                        <span className="text-xs font-semibold text-foreground min-w-0">
                          {resumeBoostPercent != null
                            ? (() => {
                                const pct = `${resumeBoostPercent}%`
                                const idx = coachDisplayed.indexOf(pct)
                                if (idx === -1) return <>{coachDisplayed}</>
                                return (
                                  <>
                                    {coachDisplayed.slice(0, idx)}
                                    <span className="text-green-600 dark:text-green-400">
                                      {pct}
                                    </span>
                                    {coachDisplayed.slice(idx + pct.length)}
                                  </>
                                )
                              })()
                            : coachDisplayed}
                        </span>
                      </div>
                      {resumeBoostPercent != null && (
                        <p className="ml-5 text-xs text-muted-foreground leading-snug">
                          We've optimized the resume to highlight transferable experience matching
                          this role.
                        </p>
                      )}
                    </div>
                    {onDownloadGeneratedResume && (
                      <Button
                        size="sm"
                        className="h-7 shrink-0 self-center text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                        onClick={() => onDownloadGeneratedResume(job.id)}
                      >
                        <Download className="mr-1 h-3 w-3" />
                        Download Tailored Resume
                      </Button>
                    )}
                  </div>
                ) : (
                  /* Heading row: icon + typing text (while animating or when no delta) */
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Sparkles
                      key={iconKey}
                      className="h-3.5 w-3.5 text-sky-500 shrink-0"
                      style={
                        coachPhase === 'dots'
                          ? {
                              animationName: 'spin',
                              animationDuration: '0.55s',
                              animationTimingFunction: 'ease-in-out',
                              animationIterationCount: 1,
                              animationFillMode: 'none',
                            }
                          : undefined
                      }
                    />
                    {coachPhase === 'dots' ? (
                      <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                        <span
                          className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                          style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                        />
                        <span
                          className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                          style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                        />
                        <span
                          className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                          style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                        />
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-foreground min-w-0">
                        {resumeBoostPercent != null
                          ? (() => {
                              const pct = `${resumeBoostPercent}%`
                              const idx = coachDisplayed.indexOf(pct)
                              if (idx === -1) return <>{coachDisplayed}</>
                              return (
                                <>
                                  {coachDisplayed.slice(0, idx)}
                                  <span className="text-green-600 dark:text-green-400">{pct}</span>
                                  {coachDisplayed.slice(idx + pct.length)}
                                </>
                              )
                            })()
                          : coachDisplayed}
                        {coachPhase === 'typing' && (
                          <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Learn block — independent of boost %; types after tailored heading finishes */}
              {learnAfterCoverageText && (
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-sky-200/50 dark:border-sky-700/40">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Sparkles
                      key={learnIconKey}
                      className="h-3.5 w-3.5 text-sky-500 shrink-0"
                      style={
                        learnPhase === 'dots'
                          ? {
                              animationName: 'spin',
                              animationDuration: '0.55s',
                              animationTimingFunction: 'ease-in-out',
                              animationIterationCount: 1,
                              animationFillMode: 'none',
                            }
                          : undefined
                      }
                    />
                    {learnPhase === 'dots' ? (
                      <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                        <span
                          className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                          style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                        />
                        <span
                          className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                          style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                        />
                        <span
                          className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                          style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                        />
                      </span>
                    ) : (
                      <span className="text-xs text-foreground/80 min-w-0">
                        {learnDisplayed}
                        {learnPhase === 'typing' && (
                          <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                        )}
                      </span>
                    )}
                  </div>

                  {(learnPhase === 'typing' || learnPhase === 'done') && (
                    <span
                      className={cn(
                        'shrink-0',
                        learnAllBlockedWhileGeneratingElsewhere &&
                          !isCuratingLessons &&
                          'inline-flex cursor-not-allowed',
                      )}
                      title={
                        learnAllBlockedWhileGeneratingElsewhere &&
                        !isCuratingLessons &&
                        learnAllBlockedTooltip
                          ? learnAllBlockedTooltip
                          : undefined
                      }
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
                        onClick={() => {
                          if (!isCuratingLessons && onLearnAll) onLearnAll()
                        }}
                        disabled={isCuratingLessons || learnAllBlockedWhileGeneratingElsewhere}
                      >
                        {isCuratingLessons ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            Curating lessons
                          </>
                        ) : (
                          <>
                            <BookOpen className="mr-1 h-3 w-3" />
                            {learnAllLabel ?? 'Learn all skills'}
                          </>
                        )}
                      </Button>
                    </span>
                  )}
                </div>
              )}

              {learnAllError && <p className="text-xs text-red-600">{learnAllError}</p>}
            </div>
          ) : resumeFailed ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">Resume generation failed</span>
              {onRetryGenerateResume && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onRetryGenerateResume(job.id)}
                >
                  Retry
                </Button>
              )}
            </div>
          ) : null}
          {generateResumeError && (
            <p className="text-xs text-red-600 mt-1.5">{generateResumeError}</p>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        {applyConfirmed ? (
          <div className="flex items-center gap-1.5 text-sm text-sky-600">
            <Sparkles className="h-3.5 w-3.5 shrink-0" />
            <span>Application in progress</span>
          </div>
        ) : (
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            onClick={() => setShowApplyConfirm(true)}
          >
            Apply <ExternalLink className="ml-1 h-3.5 w-3.5" />
          </Button>
        )}

        {/* Learn all skills — only when not already shown in the coach strip */}
        {showLearnInActionBar && match && (
          <div className="flex flex-col items-start gap-1">
            <span
              className={cn(
                learnAllBlockedWhileGeneratingElsewhere &&
                  !isCuratingLessons &&
                  'inline-flex cursor-not-allowed',
              )}
              title={
                learnAllBlockedWhileGeneratingElsewhere &&
                !isCuratingLessons &&
                learnAllBlockedTooltip
                  ? learnAllBlockedTooltip
                  : undefined
              }
            >
              <Button
                size="sm"
                variant="outline"
                className="bg-white hover:bg-sky-50 text-foreground border-sky-300 dark:bg-white dark:hover:bg-sky-100 dark:border-sky-300"
                onClick={() => {
                  if (!isCuratingLessons && onLearnAll) onLearnAll()
                }}
                disabled={isCuratingLessons || learnAllBlockedWhileGeneratingElsewhere}
              >
                {isCuratingLessons ? (
                  <>
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    Curating lessons
                  </>
                ) : (
                  <>
                    <BookOpen className="mr-1 h-3.5 w-3.5" />
                    {learnAllLabel ?? 'Learn all skills'}
                  </>
                )}
              </Button>
            </span>
            {learnAllError ? (
              <p className="text-xs text-red-600 max-w-[280px]">{learnAllError}</p>
            ) : null}
          </div>
        )}

        {onViewDetails && !hasScore && (
          <Button variant="outline" size="sm" onClick={() => onViewDetails(job.id)}>
            <PanelRight className="mr-1.5 h-3.5 w-3.5 opacity-80" />
            View job details
          </Button>
        )}
      </div>

      <Dialog open={showApplyConfirm} onOpenChange={setShowApplyConfirm}>
        <DialogContent className="max-w-sm">
          <div className="flex items-center gap-2 bg-sky-50 border-b border-sky-100 px-5 py-3 -mx-6 -mt-6 rounded-t-lg">
            <Sparkles className="h-4 w-4 text-sky-500 shrink-0" />
            <span className="text-sm font-semibold tracking-tight">Pelgo coach</span>
          </div>

          <DialogHeader className="pt-2">
            <DialogTitle className="sr-only">Confirm application</DialogTitle>
            <DialogDescription className="text-sm text-foreground leading-relaxed">
              {applyConfirmed ? (
                'Applying on your behalf. I will notify you directly for any updates.'
              ) : (
                <>
                  I'll head over to {job.company ? <strong>{job.company}</strong> : 'the employer'}
                  's page and apply on your behalf using your resume and filling in any extra
                  details from your profile along the way.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            {applyConfirmed ? (
              <Button size="sm" onClick={() => setShowApplyConfirm(false)}>
                Got it
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowApplyConfirm(false)}>
                  Not now
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setApplyConfirmed(true)}
                >
                  Let's go
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
